import { ref, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { getAllOrgs } from '@/services/orgService';
import { getRouteSchemaName, isStaffOrServiceProvider, validateSchemaAccess } from '@/utils/schemaAccess';

// State lives outside the function - shared across all instances
const organizations = ref([]);
const currentOrganizationId = ref(null);
const isLoading = ref(false);

export function useMultiTenant() {
    const router = useRouter();
    const route = useRoute();

    const currentOrganization = computed(() => 
        organizations.value.find(org => org.id === currentOrganizationId.value)
    );

    const currentSchemaName = computed(() => 
        currentOrganization.value?.schema_name || null
    );

    const fetchOrganizations = async () => {
        try {
            const orgs = await getAllOrgs();
            const filtered = orgs.filter(org => !org.is_service_provider);

            organizations.value = (filtered.length > 0 ? filtered : orgs)
                .sort((a, b) => a.name.localeCompare(b.name));

            const currentOrgStillExists = organizations.value.some(
                org => org.id === currentOrganizationId.value
            );
            if (!currentOrgStillExists && organizations.value.length > 0) {
                // Honor a ?client= deep-link / refresh: pin the org named in the
                // URL when it matches one we can see, else fall back to the first
                // org. Previously this always pinned organizations.value[0],
                // which clobbered the URL's tenant on hard navigation (BUG-1):
                // SiteView.mounted() calls fetchOrganizations() before
                // loadInitialData(), so the URL-honoring path was never reached.
                const schemaFromUrl = getRouteSchemaName(route);
                const target = (schemaFromUrl &&
                    organizations.value.find(o => o.schema_name === schemaFromUrl))
                    || organizations.value[0];
                await switchOrganization(target.id, async () => {});
            }
        } catch (error) {
            // Error handled by view making use of this composable
        }
    };

    const switchOrganization = async (organizationId, fetchDataFunction) => {
        if (currentOrganizationId.value === organizationId) return;
        
        currentOrganizationId.value = organizationId;
        isLoading.value = true;
        
        try {
            const org = organizations.value.find(o => o.id === organizationId);
            
            if (isStaffOrServiceProvider()) {
                router.replace({ query: { ...route.query, client: org.schema_name } });
            }
            
            await fetchDataFunction(org.schema_name);
        } catch (error) {
            // Error handled by view making use of this composable
        } finally {
            isLoading.value = false;
        }
    };

    const loadInitialData = async (fetchDataFunction) => {
        try {
            if (!isStaffOrServiceProvider() || organizations.value.length === 0) {
                await fetchDataFunction();
                return;
            }
        
            const schemaFromUrl = getRouteSchemaName(route);

            // If already have a selected org and no schema in URL, keep current org
            if (currentOrganizationId.value && !schemaFromUrl) {
                await fetchDataFunction(currentSchemaName.value);
                return;
            }

            const org = schemaFromUrl 
                ? organizations.value.find(o => o.schema_name === schemaFromUrl)
                : organizations.value[0];
            
            await switchOrganization(org.id, fetchDataFunction);
        } catch (error) {
            // Error handled by view making use of this composable
        }
    };

    const getDetailPageParam = (additionalParams = {}) => {
        if (!isStaffOrServiceProvider() || !currentOrganizationId.value) return additionalParams;
        
        return {
            ...additionalParams,
            org: currentOrganizationId.value,
            client: currentSchemaName.value
        };
    };
  
    const addSchemaParam = () => {
        if (!isStaffOrServiceProvider() || !currentSchemaName.value) return {};
        return { client: currentSchemaName.value };
    };

    const validateSchemaForOperation = () => {
        const validation = validateSchemaAccess(route);
        return {
            ...validation,
            client: validation.schemaName
        };
    };

    const getOrganizationName = (organizationId) => {
        const org = organizations.value.find(o => o.id === organizationId);
        return org?.name || 'Redacted';
    };

    const syncUrlParam = async () => {
        if (!isStaffOrServiceProvider() || !currentOrganizationId.value) return;
        const org = organizations.value.find(o => o.id === currentOrganizationId.value);
        if (org) {
            router.replace({ query: { ...route.query, client: org.schema_name } });
        }
    };

    return {
        organizations,
        currentOrganizationId,
        currentSchemaName,
        isLoading,
        fetchOrganizations,
        switchOrganization,
        loadInitialData,
        getDetailPageParam,
        addSchemaParam,
        validateSchemaForOperation,
        getOrganizationName,
        syncUrlParam
    };
}